import React, {
  useImperativeHandle,
  useRef,
  useEffect,
  forwardRef,
  useState,
} from "react";
import { Modal } from "bootstrap";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import HttpAPICall from "../../services/HttpAPICall";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../components/ui/Loader/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const ModuleSubModuleUpdatePopup = forwardRef(({ onUpdateSuccess }, ref) => {
  const access_token = useSelector((state) => state.auth.access_token);
  const modalElem = useRef(null);
  const searchModal = useRef(null);
  const [formLoading, setFormLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [tillDate, setTillDate] = useState(null);

  const moduleTextareaRef = useRef(null);
  const subModuleTextareaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openEditModal: (data) => {
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && typeof data === "object") list = [data];

      if (list.length === 0) {
        toast.error("Nothing selected", { position: toast.POSITION.TOP_RIGHT });
        return;
      }

      setItems(list);
      setTillDate(null);
      searchModal.current.show();
    },
    closeModal: () => searchModalClose(),
  }));

  useEffect(() => {
    modalElem.current = document.getElementById("subModuleEditModalID");
    searchModal.current = new Modal(modalElem.current, {
      keyboard: false,
      backdrop: "static",
    });

    const handleHidden = () => {
      setFormLoading(false);
      setItems([]);
      setTillDate(null);
    };

    const handleShown = () => {
      setTimeout(() => {
        autoGrow(moduleTextareaRef.current);
        autoGrow(subModuleTextareaRef.current);
      }, 50);
    };

    modalElem.current.addEventListener("hidden.bs.modal", handleHidden);
    modalElem.current.addEventListener("shown.bs.modal", handleShown);

    return () => {
      if (searchModal.current) searchModal.current.dispose();
      if (modalElem.current) {
        modalElem.current.removeEventListener("hidden.bs.modal", handleHidden);
        modalElem.current.removeEventListener("shown.bs.modal", handleShown);
      }
    };
  }, []);

  const searchModalClose = () => {
    if (searchModal.current) searchModal.current.hide();
    setFormLoading(false);
    setItems([]);
    setTillDate(null);
  };

  const moduleNames = items
    .filter((it) => it.type === "module")
    .map((it) => it.name)
    .join(", ");

  const subModuleNames = items
    .filter((it) => it.type === "sub_module")
    .map((it) => it.name)
    .join(", ");

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  useEffect(() => {
    autoGrow(moduleTextareaRef.current);
    autoGrow(subModuleTextareaRef.current);
  }, [moduleNames, subModuleNames, formLoading, items]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.warning("Please select at least one item", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    if (!tillDate) {
      toast.error("Till date is required", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setFormLoading(true);

    const formattedTill = moment(tillDate).format("YYYY-MM-DD");

    const selectedModuleIds = items
      .filter((it) => it.type === "module")
      .map((it) => it.id);

    // Group sub-modules by their parent module_id
    const subModulesByModule = {};
    items
      .filter((it) => it.type === "sub_module")
      .forEach((it) => {
        const mId = it.module_id;
        if (!subModulesByModule[mId]) subModulesByModule[mId] = [];
        subModulesByModule[mId].push(it);
      });

    // All module ids we need in the payload
    const allModuleIds = Array.from(
      new Set([
        ...selectedModuleIds,
        ...Object.keys(subModulesByModule).map((k) => Number(k)),
      ])
    );

    // Lookup: moduleId → module item (from `items` when module is selected)
    const selectedModuleById = {};
    items
      .filter((it) => it.type === "module")
      .forEach((it) => {
        selectedModuleById[it.id] = it;
      });

    const modulesPayload = allModuleIds.map((mId) => {
      const isModuleSelected = selectedModuleIds.includes(mId);
      const subItems = subModulesByModule[mId] || [];

      // Module's from date
      let moduleFrom = "";
      if (isModuleSelected && selectedModuleById[mId]?.from) {
        moduleFrom = moment(selectedModuleById[mId].from).format("YYYY-MM-DD");
      } else if (subItems[0]?.module_from) {
        // Fallback: pick module_from from any selected sub-module of this module
        moduleFrom = moment(subItems[0].module_from).format("YYYY-MM-DD");
      }

      return {
        module_id: mId,
        module_from_date: moduleFrom, // 👈 real from date
        module_till_date: isModuleSelected ? formattedTill : "",
        is_edit: isModuleSelected ? "Y" : "N",
        sub_modules: subItems.map((sub) => ({
          sub_module_id: sub.id,
          from_date: sub.from ? moment(sub.from).format("YYYY-MM-DD") : "",
          till_date: formattedTill,
        })),
      };
    });

    const payload = {
      tap_master_id: items[0]?.tap_master_id || null,
      modules: modulesPayload,
    };

    HttpAPICall.withAthorization(
      "PUT",
      BACKOFFICE_BASE_URL + "/group/update_module_sub",
      access_token,
      {},
      payload,
      () => {
        toast.success(
          `${items.length} item${items.length > 1 ? "s" : ""} updated successfully`,
          { position: toast.POSITION.TOP_RIGHT }
        );
        if (onUpdateSuccess) onUpdateSuccess();
        searchModalClose();
      },
      (error) => {
        toast.error(error?.message || "Failed to update", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    ).finally(() => setFormLoading(false));
  };

  return (
    <div className="modal fade" id="subModuleEditModalID" tabIndex="-1" ref={modalElem}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Update Module / Sub-Module</h5>
            <button
              type="button"
              className="btn-close"
              onClick={searchModalClose}
              aria-label="Close"
              disabled={formLoading}
            />
          </div>

          <div className="modal-body">
            {formLoading ? (
              <div className="text-center py-5">
                <Loader />
              </div>
            ) : (
              <form id="subModuleEditForm" onSubmit={handleSubmit}>
                <div className="row">
                  {/* Module */}
                  <div className="row align-items-start py-2 justify-content-center">
                    <label className="col-sm-3 form-label">Module</label>
                    <div className="col-sm-8">
                      <textarea
                        ref={moduleTextareaRef}
                        className="form-control"
                        value={moduleNames || "-"}
                        readOnly
                        rows={1}
                        style={{
                          backgroundColor: "#e9ecef",
                          resize: "none",
                          overflow: "hidden",
                          minHeight: "38px",
                          boxSizing: "border-box",
                          lineHeight: "1.5",
                        }}
                      />
                    </div>
                  </div>

                  {/* Sub-Module */}
                  <div className="row align-items-start py-2 justify-content-center">
                    <label className="col-sm-3 form-label">Sub-Module</label>
                    <div className="col-sm-8">
                      <textarea
                        ref={subModuleTextareaRef}
                        className="form-control"
                        value={subModuleNames || "-"}
                        readOnly
                        rows={1}
                        style={{
                          backgroundColor: "#e9ecef",
                          resize: "none",
                          overflow: "hidden",
                          minHeight: "38px",
                          boxSizing: "border-box",
                          lineHeight: "1.5",
                        }}
                      />
                    </div>
                  </div>

                  {/* Till Date */}
                  <div className="row align-items-center py-2 justify-content-center">
                    <label className="col-sm-3 form-label require">Till Date</label>
                    <div className="col-sm-8">
                      <DatePicker
                        selected={tillDate}
                        onChange={(date) => setTillDate(date)}
                        dateFormat="dd-MMM-yyyy"
                        className="form-control"
                        showMonthDropdown
                        showYearDropdown
                        autoComplete="off"
                        scrollMonthYearDropdown
                        placeholderText="Select Till Date"
                        required
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={searchModalClose}
              disabled={formLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="subModuleEditForm"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Updating...
                </>
              ) : items.length > 1 ? (
                `Update All (${items.length})`
              ) : (
                "Update"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ModuleSubModuleUpdatePopup;